import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReconciliationHealth, ReconciliationStatus } from "@/types/pms";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/utils/dateUtils";
import { Loader2, RefreshCw } from "lucide-react";

interface ReconciliationCardProps {
  status: ReconciliationStatus;
  isTriggering: boolean;
  onTrigger: () => void;
}

type HealthDisplay = { className: string; key: string };

const healthConfig: Record<ReconciliationHealth, HealthDisplay> = {
  [ReconciliationHealth.HEALTHY]: {
    className: "bg-green-100 text-green-800",
    key: "pms.stats.reconciliation.healthy",
  },
  [ReconciliationHealth.NEEDS_ATTENTION]: {
    className: "bg-yellow-100 text-yellow-800",
    key: "pms.stats.reconciliation.needsAttention",
  },
  [ReconciliationHealth.CRITICAL]: {
    className: "bg-red-100 text-red-800",
    key: "pms.stats.reconciliation.critical",
  },
};

const fallbackHealth: HealthDisplay = {
  className: "bg-gray-100 text-gray-800",
  key: "pms.stats.reconciliation.unknown",
};

const getHealthDisplay = (health?: ReconciliationHealth): HealthDisplay =>
  (health && healthConfig[health]) || fallbackHealth;

type OverallBadge = {
  variant: React.ComponentProps<typeof Badge>["variant"];
  key: string;
};

const getOverallBadge = (status: ReconciliationStatus): OverallBadge => {
  if (!status.enabled) {
    return { variant: "new_outline", key: "pms.status.disabled" };
  }
  if (status.hasDrift) {
    return {
      variant: "new_destructive",
      key: "pms.stats.reconciliation.driftDetected",
    };
  }
  return {
    variant: "filled_status_completed",
    key: "pms.stats.reconciliation.healthy",
  };
};

export const ReconciliationCard = ({
  status,
  isTriggering,
  onTrigger,
}: ReconciliationCardProps) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const overall = getOverallBadge(status);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("pms.stats.reconciliation.title")}</CardTitle>
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={isTriggering}>
                {isTriggering ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t("pms.stats.reconciliation.trigger")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("pms.stats.reconciliation.triggerConfirmTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("pms.stats.reconciliation.triggerConfirmDescription")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button
                  disabled={isTriggering}
                  onClick={() => {
                    onTrigger();
                    setConfirmOpen(false);
                  }}
                >
                  {t("pms.stats.reconciliation.trigger")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">
            {t("pms.stats.reconciliation.overall")}:
          </span>
          <Badge variant={overall.variant}>{t(overall.key)}</Badge>
        </div>

        <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>
            {t("pms.stats.reconciliation.lastRun")}:{" "}
            {formatRelativeTime(status.lastReconciliation) ||
              t("pms.status.never")}
          </span>
          <span>
            {t("pms.stats.reconciliation.driftChecked")}:{" "}
            {formatRelativeTime(status.driftCheckedAt ?? undefined) ||
              t("pms.status.never")}
          </span>
        </div>

        {status.entitySummaries.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("pms.stats.reconciliation.entityType")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pms.stats.reconciliation.mappedCount")}
                </TableHead>
                <TableHead>{t("pms.stats.reconciliation.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {status.entitySummaries.map((entity) => {
                const entityHealth = getHealthDisplay(entity.status);
                return (
                  <TableRow key={entity.entityType}>
                    <TableCell>
                      <Badge variant="outline">{entity.entityType}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {entity.mappedCount}
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("border-0", entityHealth.className)}>
                        {t(entityHealth.key)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
